import React, { useEffect, useMemo, useState } from "react";
import { Layout, Row, Col, Skeleton, Table, Button, Select, Modal } from "antd";
import { ApiHelper, getToken, getUserData } from "../../../../utils/Helpers";
import HorizontalMenu from "../../../layout/Horizontalmenu";
import { ApiRoutes } from "../../../../utils/Config/ApiRoutes";
import Emptymsg from "../../../layout/message/Emptymsg";
import { messages } from "../../../../utils/Common/Messages";
import { AppConfig } from "../../../../utils/Config/AppConfig";
import { DownloadOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import Rangepicker from "../../../layout/filter/Rangepicker";
import moment from "moment";
import "./timeClockManagement.less";
import EditClockTime from "./editClockTime";
const { Content } = Layout;
const { Option } = Select;

const TimeClockManagement = () => {
  const { language } = getUserData();
  const userData = getUserData();
  const organizationId = userData ? userData.organizationId : 0;
  const lan = language ?? "EN";
  const [filterData, setFilterData] = useState({
    startDate: moment().format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
    search: "",
    employeeId: "",
    perPage: 10,
    page: 1,
  });
  const [data, setData] = React.useState([]);
  const [breakActivities, setBreakActivities] = React.useState([]);
  const [employeeData, setEmployeeData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editData, setEditData] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [activityModalVisible, setActivityModalVisible] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());

  const parseTime = (value) => {
    if (!value) {
      return null;
    }
    const time = moment(value);
    return time.isValid() ? time : null;
  };

  const formatDateTime = (value) => {
    const time = parseTime(value);
    return time ? time.format("YYYY-MM-DD HH:mm") : "-";
  };

  const formatMinutes = (minutes) => {
    const validMinutes = Number.isFinite(minutes)
      ? Math.max(0, Math.floor(minutes))
      : 0;
    const hours = Math.floor(validMinutes / 60);
    const mins = validMinutes % 60;
    return `${hours}:${mins.toString().padStart(2, "0")}`;
  };

  const formatMinutesWithTotal = (minutes) => {
    const validMinutes = Number.isFinite(minutes)
      ? Math.max(0, Math.floor(minutes))
      : 0;
    return `${formatMinutes(validMinutes)} (${validMinutes} min)`;
  };

  const formatHoursOrMinutes = (minutes) => {
    const validMinutes = Number.isFinite(minutes)
      ? Math.max(0, Math.floor(minutes))
      : 0;
    if (validMinutes < 60) {
      return `${validMinutes}mins`;
    }

    const hours = Math.floor(validMinutes / 60);
    const mins = validMinutes % 60;
    const hourLabel = hours === 1 ? "hr" : "hrs";

    if (mins === 0) {
      return `${hours} ${hourLabel}`;
    }

    return `${hours} ${hourLabel} ${mins}mins`;
  };

  const getActivityEntries = (item) => {
    const entries = [];
    const type = item.clockOutType || "";
    const isBreakType = type === "Break" || type === "Resume from Break";

    if (isBreakType) {
      if (item.clockIn) {
        entries.push({
          type: "Break Start",
          time: item.clockIn,
          sortTime: parseTime(item.clockIn),
        });
      }
      if (item.clockOut) {
        entries.push({
          type: "Break End",
          time: item.clockOut,
          sortTime: parseTime(item.clockOut),
        });
      }
      return entries;
    }

    if (item.clockIn) {
      entries.push({
        type: "Clock-in",
        time: item.clockIn,
        sortTime: parseTime(item.clockIn),
      });
    }
    if (item.clockOut) {
      entries.push({
        type: "Clock-out",
        time: item.clockOut,
        sortTime: parseTime(item.clockOut),
      });
    }
    return entries;
  };

  const buildDailySummary = (rawData) => {
    const breakMap = breakActivities.reduce((acc, item) => {
      const parentId =
        item.parentTimeClockManagmentId || item.parentTimeClockManagementId;
      if (!parentId) {
        return acc;
      }
      if (!acc[parentId]) {
        acc[parentId] = [];
      }
      acc[parentId].push(item);
      return acc;
    }, {});

    const grouped = {};

    rawData.forEach((item, index) => {
      const referenceTime = parseTime(item.clockIn) || parseTime(item.clockOut);
      if (!referenceTime) {
        return;
      }

      const dateKey = referenceTime.format("YYYY-MM-DD");
      const employeeId =
        item.employeeId ||
        `${item.firstName || ""}-${item.lastName || ""}-${index}`;
      const key = `${employeeId}-${dateKey}`;

      if (!grouped[key]) {
        grouped[key] = {
          key,
          employeeId,
          employeeName: `${item.firstName || ""} ${item.lastName || ""}`.trim(),
          employeePin: item.employeePin,
          date: dateKey,
          clockIn: null,
          clockOut: null,
          breakStart: null,
          breakEnd: null,
          status: "-",
          workingMinutes: 0,
          breakMinutes: 0,
          note: item.note || "",
          activities: [],
          editableRecord: {
            timeClockManagementId: item.timeClockManagementId,
            clockIn: item.clockIn,
            clockOut: item.clockOut,
            note: item.note,
          },
        };
      }

      const row = grouped[key];
      const type = item.clockOutType || "";
      const isBreakType = type === "Break" || type === "Resume from Break";

      if (item.note) {
        row.note = item.note;
      }

      if (!isBreakType && item.timeClockManagementId) {
        row.editableRecord = {
          timeClockManagementId: item.timeClockManagementId,
          clockIn: item.clockIn,
          clockOut: item.clockOut,
          note: item.note,
        };
      }

      if (!isBreakType && item.clockIn) {
        const currentClockIn = parseTime(row.clockIn);
        const nextClockIn = parseTime(item.clockIn);
        if (
          !currentClockIn ||
          (nextClockIn && nextClockIn.isBefore(currentClockIn))
        ) {
          row.clockIn = item.clockIn;
        }
      }

      if (!isBreakType && item.clockOut) {
        const currentClockOut = parseTime(row.clockOut);
        const nextClockOut = parseTime(item.clockOut);
        if (
          !currentClockOut ||
          (nextClockOut && nextClockOut.isAfter(currentClockOut))
        ) {
          row.clockOut = item.clockOut;
        }
      }

      if (isBreakType && item.clockIn) {
        const currentBreakStart = parseTime(row.breakStart);
        const nextBreakStart = parseTime(item.clockIn);
        if (
          !currentBreakStart ||
          (nextBreakStart && nextBreakStart.isAfter(currentBreakStart))
        ) {
          row.breakStart = item.clockIn;
        }
      }

      if (isBreakType && item.clockOut) {
        const currentBreakEnd = parseTime(row.breakEnd);
        const nextBreakEnd = parseTime(item.clockOut);
        if (
          !currentBreakEnd ||
          (nextBreakEnd && nextBreakEnd.isAfter(currentBreakEnd))
        ) {
          row.breakEnd = item.clockOut;
        }
      }

      row.activities = row.activities.concat(getActivityEntries(item));

      const itemBreaks = breakMap[item.timeClockManagementId] || [];
      itemBreaks.forEach((breakItem) => {
        row.activities = row.activities.concat(getActivityEntries(breakItem));
      });
    });

    // Helper: get the note for a break pair (by start time)
    function getBreakNoteForPair(row, breakMap, breakStart) {
      // Find all break activities for this row
      let allBreaks = [];
      if (row.editableRecord && row.editableRecord.timeClockManagementId) {
        const breaks = breakMap[row.editableRecord.timeClockManagementId] || [];
        allBreaks = allBreaks.concat(breaks);
      }
      // Also check if this row itself is a break activity
      if (row.clockOutType === "Break" || row.clockOutType === "Resume from Break") {
        allBreaks.push(row);
      }
      // Find the break with matching clockIn
      const found = allBreaks.find(b => b.clockIn && parseTime(b.clockIn).isSame(breakStart));
      return found && found.note ? found.note : "";
    }

    const now = moment(nowTick);

    return Object.values(grouped)
      .map((item) => {
        const orderedActivities = item.activities
          .filter((activity) => activity.sortTime)
          .sort((a, b) => {
            const timeDiff = a.sortTime.valueOf() - b.sortTime.valueOf();
            if (timeDiff !== 0) {
              return timeDiff;
            }

            const priority = {
              "Clock-in": 1,
              "Break Start": 2,
              "Break End": 3,
              "Clock-out": 4,
            };
            return (priority[a.type] || 99) - (priority[b.type] || 99);
          });

        let breakMinutes = 0;
        let openBreak = null;
        const breakPairs = [];

        orderedActivities.forEach((activity) => {
          if (activity.type === "Break Start") {
            // If there is already an open break, ignore it (do not double count)
            if (!openBreak) {
              openBreak = activity.sortTime;
            }
          }

          if (activity.type === "Break End" && openBreak) {
            const duration = Math.max(
              activity.sortTime.diff(openBreak, "minutes", true),
              0,
            );
            breakMinutes += duration;
            breakPairs.push({ start: openBreak, end: activity.sortTime });
            openBreak = null;
          }
        });

        // Only count open break if there is a clockOut (shift ended), otherwise ignore incomplete break
        if (openBreak && item.clockOut) {
          const endTime = parseTime(item.clockOut);
          if (endTime && endTime.isAfter(openBreak)) {
            const duration = Math.max(
              endTime.diff(openBreak, "minutes", true),
              0,
            );
            breakMinutes += duration;
            breakPairs.push({ start: openBreak, end: endTime });
          }
        }

        const clockInTime = parseTime(item.clockIn);
        const clockOutTime = parseTime(item.clockOut);
        const endTime = clockOutTime;

        if (endTime == null || !endTime) {
        }

        let totalMinutes = 0;
        let workingMinutes = null;

        if (clockInTime && endTime && endTime.isAfter(clockInTime)) {
          totalMinutes = endTime.diff(clockInTime, "minutes", true);
        }
        if (
          clockInTime &&
          !clockOutTime &&
          !openBreak &&
          breakPairs.length === 0
        ) {
          // Only clock-in, no clock-out or break
          workingMinutes = null;
        } else if (clockInTime && openBreak && breakPairs.length === 0) {
          // Clock-in and break-in, but no break-out or clock-out
          workingMinutes = Math.max(
            openBreak.diff(clockInTime, "minutes", true),
            0,
          );
        } else if (clockOutTime) {
          workingMinutes = Math.max(totalMinutes - breakMinutes, 0);
        } else if (clockInTime && breakPairs.length > 0 && !clockOutTime) {
          // If there are completed breaks but no clock-out, working minutes is from clock-in to first break start
          workingMinutes = Math.max(
            breakPairs[0].start.diff(clockInTime, "minutes", true),
            0,
          );
        } else {
          workingMinutes = null;
        }

        let status = "-";
        if (clockOutTime) {
          status = "Clocked Out";
        } else if (openBreak) {
          status = "On Break";
        } else if (clockInTime) {
          status = "Clocked In";
        }

        return {
          ...item,
          activities: orderedActivities,
          activityRows: [
            {
              key: `${item.key}-shift`,
              inLabel: "Clock In",
              inTime: formatDateTime(item.clockIn),
              outLabel: "Clock Out",
              outTime: formatDateTime(item.clockOut),
            },
            // Show all completed break pairs
            ...breakPairs.map((pair, idx) => ({
              key: `${item.key}-break-${idx + 1}`,
              inLabel: idx === 0 ? "Break Start(s)" : "",
              inTime: formatDateTime(pair.start),
              outLabel: idx === 0 ? "Break End(s)" : "",
              outTime: formatDateTime(pair.end),
              noteLabel : idx === 0 ? "Note(s)" : "",
              note: getBreakNoteForPair(item, breakMap, pair.start),
            })),
            // If there is an open break (start with no end), show it as a row with '-' for end
            ...(openBreak && !item.clockOut
              ? [
                  {
                    key: `${item.key}-break-unended`,
                    inLabel: breakPairs.length === 0 ? "Break Start(s)" : "",
                    inTime: formatDateTime(openBreak),
                    outLabel: breakPairs.length === 0 ? "Break End(s)" : "",
                    outTime: "-",
                    noteLabel: breakPairs.length === 0 ? "Note(s)" : "",
                    note: getBreakNoteForPair(item, breakMap, openBreak),
                  },
                ]
              : []),
          ],
          breakMinutes: Math.floor(breakMinutes),
          workingMinutes:
            workingMinutes === null ? null : Math.floor(workingMinutes),
          status,
        };
      })
      .sort((a, b) => {
        const first = `${a.date} ${a.employeeName}`;
        const second = `${b.date} ${b.employeeName}`;
        return first > second ? -1 : 1;
      });
  };

  const consolidatedData = useMemo(
    () => buildDailySummary(data),
    [data, breakActivities, nowTick],
  );
  const paginatedData = useMemo(() => {
    const start = (filterData.page - 1) * filterData.perPage;
    return consolidatedData.slice(start, start + filterData.perPage);
  }, [consolidatedData, filterData.page, filterData.perPage]);

  useEffect(() => {
    getData();
    // eslint-disable-next-line
  }, [
    filterData.startDate,
    filterData.endDate,
    filterData.search,
    filterData.employeeId,
  ]);

  useEffect(() => {
    const refreshInterval = setInterval(() => {
      getData(true);
    }, 5000);

    return () => {
      clearInterval(refreshInterval);
    };
    // eslint-disable-next-line
  }, [
    filterData.startDate,
    filterData.endDate,
    filterData.search,
    filterData.employeeId,
  ]);

  useEffect(() => {
    const timer = setInterval(() => {
      setNowTick(Date.now());
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    getEmployeeList();
    // eslint-disable-next-line
  }, []);

  const searchData = (
    sDate = filterData.startDate,
    eDate = filterData.endDate,
  ) => {
    setFilterData({ ...filterData, startDate: sDate, endDate: eDate });
  };

  const nextData = (pagination) => {
    setFilterData({
      ...filterData,
      perPage: pagination.pageSize,
      page: pagination.current,
    });
  };
  const getData = async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setData([]);
      setBreakActivities([]);
    }
    const response = await new ApiHelper().FetchFromServer(
      ApiRoutes.TIME_CLOCK_LIST.service,
      ApiRoutes.TIME_CLOCK_LIST.url,
      ApiRoutes.TIME_CLOCK_LIST.method,
      ApiRoutes.TIME_CLOCK_LIST.authenticate,
      {
        gmtTime: userData.time_gmt,
        employeeId: filterData.employeeId,
        organizationId: organizationId,
        startDate: filterData.startDate,
        endDate: filterData.endDate,
        search: filterData.search,
        limit: 1000,
        offset: 1,
      },
    );
    if (response.code === 200) {
      if (response.data.data) {
        const sourceData = response.data.data ?? [];
        const filteredData = sourceData.filter(
          (item) =>
            item.clockOutType !== "Break" &&
            item.clockOutType !== "Resume from Break",
        );
        const childBreakActivities = sourceData.filter(
          (item) =>
            (item.clockOutType === "Break" ||
              item.clockOutType === "Resume from Break") &&
            (item.parentTimeClockManagmentId ||
              item.parentTimeClockManagementId),
        );
        setData(filteredData);
        setBreakActivities(childBreakActivities);
      } else {
        setData([]);
        setBreakActivities([]);
      }
    } else {
      setData([]);
      setBreakActivities([]);
    }
    setLoading(false);
  };

  const getEmployeeList = async () => {
    const response = await new ApiHelper().FetchFromServer(
      ApiRoutes.TIME_CLOCK_EMPLOYEE_LIST.service,
      ApiRoutes.TIME_CLOCK_EMPLOYEE_LIST.url,
      ApiRoutes.TIME_CLOCK_EMPLOYEE_LIST.method,
      ApiRoutes.TIME_CLOCK_EMPLOYEE_LIST.authenticate,
      {
        organizationId: organizationId,
      },
    );
    if (response.code === 200) {
      if (response.data.data) {
        setEmployeeData(response.data.data ?? []);
      } else {
        setEmployeeData([]);
      }
    } else {
      setEmployeeData([]);
    }
  };

  const editTimeClock = async (data) => {
    if (
      !data ||
      !data.editableRecord ||
      !data.editableRecord.timeClockManagementId
    ) {
      return;
    }

    setEditData({
      timeClockManagementId: data.editableRecord.timeClockManagementId,
      clockIn: data.editableRecord.clockIn,
      clockOut: data.editableRecord.clockOut,
      note: data.editableRecord.note,
    });
    setIsModalVisible(true);
  };

  const openActivityLog = (rowData) => {
    setSelectedActivity(rowData);
    setActivityModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    getData();
  };

  const columns = [
    {
      title: messages.employeeName[lan],
      dataIndex: "employeeName",
      key: "employeeName",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
    },
    {
      title: messages.userPin[lan],
      dataIndex: "employeePin",
      key: "employeePin",
    },
    {
      title: messages.clockIn[lan],
      key: "clockIn",
      dataIndex: "clockIn",
      render: (clockIn) => formatDateTime(clockIn),
    },
    {
      title: messages.clockOut[lan],
      key: "clockOut",
      dataIndex: "clockOut",
      render: (clockOut) => formatDateTime(clockOut),
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
    },
    // {
    //   title: messages.notes[lan],
    //   key: "note",
    //   dataIndex: "note",
    // },
    {
      title: messages.workingHours[lan],
      key: "workingMinutes",
      dataIndex: "workingMinutes",
      render: (workingMinutes) => formatMinutes(workingMinutes),
    },
    {
      title: messages.breakHours[lan] || "Break Hours",
      key: "breakMinutes",
      dataIndex: "breakMinutes",
      render: (breakMinutes) => formatMinutes(breakMinutes),
    },
    {
      title: "Action",
      key: "action",
      render: (e) => {
        return (
          <>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => {
                editTimeClock(e);
              }}
            ></Button>
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => {
                openActivityLog(e);
              }}
            ></Button>
          </>
        );
      },
    },
  ];
  const downloadData = () => {
    let token = getToken();
    window.open(
      `${AppConfig.API_ENDPOINT}${ApiRoutes.EXPORT_TIME_CLOCK.service}${ApiRoutes.EXPORT_TIME_CLOCK.url}?employeeId=${filterData.employeeId}&organizationId=${organizationId}&startDate=${moment(filterData.startDate).format(AppConfig.DATEFOMAT)}&endDate=${moment(filterData.endDate).format(AppConfig.DATEFOMAT)}&search=${filterData.search}&gmtTime=${userData.time_gmt}&fullActivityLog=1&token=${token}`,
      "_blank",
    );
  };

  const activityColumns = [
    {
      title: "",
      dataIndex: "inTime",
      key: "inTime",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 700, color: "#000000" }}>
            {record.inLabel}
          </div>
          <div>{record.inTime}</div>
        </div>
      ),
    },
    {
      title: "",
      dataIndex: "outTime",
      key: "outTime",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 700, color: "#000000" }}>
            {record.outLabel}
          </div>
          <div>{record.outTime}</div>
        </div>
      ),
    },
    {
      title: "",
      dataIndex: "note",
      key: "note",
      render: (note, record) =>
        <div>
          <div style={{ fontWeight: 700, color: "#000000" }}>
            {record.noteLabel}
          </div>
          <div>{note}</div>
        </div>
    },
  ];

  return (
    <>
      <HorizontalMenu />
      <Row
        className="filterBox"
        style={{ padding: "0 11px 14px 11px", margin: "0px" }}
        gutter={{ xs: 12, lg: 6 }}
      >
        <Col>
          <Rangepicker
            filterdata={searchData}
            startDate={filterData.startDate}
            endDate={filterData.endDate}
            lan={lan}
          />
        </Col>
        <Col xl={5} lg={5} xs={24}>
          <Select
            placeholder={messages.employeeName[lan]}
            showSearch
            allowClear
            showArrow="true"
            className="category"
            dropdownClassName="category"
            style={{ width: "100%" }}
            optionFilterProp="children"
            onChange={(e) =>
              setFilterData({
                ...filterData,
                employeeId: e !== undefined ? e : "",
              })
            }
          >
            {employeeData.length > 0 &&
              employeeData.map((item, key) => (
                <Option value={parseInt(item.employeeId)} key={key}>
                  {`${item.firstName} ${item.lastName}`}
                </Option>
              ))}
          </Select>
        </Col>
        <Col className="pr-0 ml-auto">
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={downloadData}
          >
            {messages.Export[lan]}
          </Button>
        </Col>
      </Row>
      <Content className="content-body" style={{ minHeight: 200 }}>
        <Row>
          <Col lg={24} xs={24}>
            <div
              className="bg-white"
              style={{
                margin: "14px 14px 0px 14px",
                padding: 14,
                minHeight: 200,
              }}
            >
              <Table
                columns={columns}
                rowKey={(record) => record.key}
                dataSource={paginatedData}
                className="timeClockManagementTable"
                pageIndex={filterData.page}
                scroll={{ x: "max-content" }}
                pagination={{
                  pageSize: filterData.perPage,
                  total: consolidatedData.length,
                  current: filterData.page,
                  pageSizeOptions: ["10", "20", "50"],
                  showSizeChanger: true,
                  locale: { items_per_page: `/ ${messages.page[lan]}` },
                }}
                locale={{
                  emptyText: loading ? (
                    <Skeleton
                      paragraph={{ rows: 5 }}
                      loading={loading}
                      active
                    ></Skeleton>
                  ) : (
                    <Emptymsg
                      emptytitle={messages.emptyTitleTimeClockManagement[lan]}
                    />
                  ),
                }}
                onChange={nextData}
              />
            </div>
          </Col>
        </Row>
      </Content>

      <Modal
        title={
          selectedActivity
            ? `Activity Log - ${selectedActivity.employeeName} (${selectedActivity.date})`
            : "Activity Log"
        }
        visible={activityModalVisible}
        onCancel={() => {
          setActivityModalVisible(false);
          setSelectedActivity(null);
        }}
        footer={null}
      >
        <div
          className="bg-white"
          style={{
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            padding: 12,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
            }}
          >
            <strong style={{ color: "#000000" }}>Total Working Hours</strong>
            <span>
              {formatHoursOrMinutes(selectedActivity?.workingMinutes || 0)}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <strong style={{ color: "#000000" }}>Total Break Hours</strong>
            <span>
              {formatHoursOrMinutes(selectedActivity?.breakMinutes || 0)}
            </span>
          </div>
        </div>
        <Table
          rowKey={(record) => record.key}
          columns={activityColumns}
          dataSource={selectedActivity ? selectedActivity.activityRows : []}
          pagination={false}
          size="small"
          locale={{
            emptyText: "No activity found",
          }}
        />
      </Modal>

      {isModalVisible ? (
        <EditClockTime
          editData={editData}
          isModalVisible={isModalVisible}
          handleCancel={handleCancel}
          lan={lan}
        />
      ) : (
        ""
      )}
    </>
  );
};
export default TimeClockManagement;
